/**
 * DATE FILTER COMPONENT
 * Reusable date filter for filtering table rows by date
 * @version 1.0.0
 * @production-ready
 */

import React, { useRef } from 'react';
import { Calendar, X } from 'lucide-react';
import { motion } from 'framer-motion';

export interface DateFilterProps {
  selectedDate: string | null;
  onDateChange: (date: string | null) => void;
  placeholder?: string;
}

export const DateFilter: React.FC<DateFilterProps> = ({
  selectedDate,
  onDateChange,
  placeholder = 'Filter by date'
}) => {
  const buttonBaseClass = "flex items-center gap-1 px-3 py-1 rounded text-xs text-white font-medium transition-all duration-200";
  const inputRef = useRef<HTMLInputElement>(null);

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const date = e.target.value;
    onDateChange(date || null);
  };

  const handleClear = () => {
    onDateChange(null);
  };

  const handleCalendarIconClick = () => {
    if (inputRef.current) {
      inputRef.current.focus();
      // Try to open the picker programmatically
      if ('showPicker' in inputRef.current && typeof (inputRef.current as any).showPicker === 'function') {
        try {
          (inputRef.current as any).showPicker();
        } catch (err) {
          // showPicker might fail if not user-initiated, that's okay
          // The focus will still allow clicking the calendar icon
        }
      }
    }
  };

  return (
    <>
      <style>{`
        /* Hide the native calendar icon but keep it clickable if needed (though we use showPicker) */
        input[type="date"]::-webkit-calendar-picker-indicator {
          opacity: 0 !important;
          display: none !important; /* We use the custom icon and showPicker API */
          -webkit-appearance: none !important;
        }
        
        /* Fix the text alignment and visibility */
        input[type="date"] {
          -webkit-appearance: none;
          -moz-appearance: textfield;
          text-align: left;
          font-family: inherit;
        }

        /* Target the value text specifically */
        input[type="date"]::-webkit-datetime-edit {
            padding: 0;
            display: flex;
            align-items: center;
        }
        
        input[type="date"]::-webkit-datetime-edit-text,
        input[type="date"]::-webkit-datetime-edit-month-field,
        input[type="date"]::-webkit-datetime-edit-day-field,
        input[type="date"]::-webkit-datetime-edit-year-field {
          color: #ffffff !important;
          -webkit-text-fill-color: #ffffff !important;
          font-weight: 700 !important;
          text-transform: uppercase;
        }

        /* Fix placeholder/empty state styling */
        input[type="date"]::-webkit-datetime-edit-fields-wrapper {
            display: flex;
            align-items: center;
        }
      `}</style>
      <div className="relative flex items-center flex-shrink-0" style={{
        filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3))',
        width: 'auto',
        minWidth: '140px'
      }}>
        {/* Custom Calendar Icon - Left Aligned */}
        <motion.button
          type="button"
          onClick={handleCalendarIconClick}
          className="absolute left-3 text-white cursor-pointer z-10"
          style={{
            background: 'transparent',
            border: 'none',
            padding: 0,
            display: 'flex',
            alignItems: 'center',
          }}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
        >
          <Calendar size={14} strokeWidth={2.5} className="text-white drop-shadow-sm" />
        </motion.button>

        {/* The Date Input */}
        <input
          ref={inputRef}
          type="date"
          value={selectedDate || ''}
          onChange={handleDateChange}
          className={`${buttonBaseClass} pl-9 text-white`}
          style={{
            background: 'var(--color-secondary-orange, #f59e0b)', /* Fallback orange */
            width: '100%',
            height: '32px', /* Match other buttons */
            fontSize: '12px',
            fontWeight: '700',
            color: '#ffffff',
            border: 'none', /* Cleaner look */
            borderRadius: '6px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            WebkitTextFillColor: '#ffffff',
            outline: 'none',
            cursor: 'pointer',
            paddingRight: selectedDate ? '28px' : '10px',
            paddingLeft: '32px', /* Space for icon */
            appearance: 'none'
          }}
          placeholder={placeholder}
          title="Filter by date"
        />
        {selectedDate && (
          <motion.button
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            whileHover={{ scale: 1.1, backgroundColor: 'rgba(255, 255, 255, 0.4)' }}
            whileTap={{ scale: 0.9 }}
            onClick={handleClear}
            className="absolute right-1 p-0.5 rounded transition-colors"
            style={{
              zIndex: 10,
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
              backdropFilter: 'blur(4px)',
              width: '18px',
              height: '18px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            title="Clear date filter"
          >
            <X size={10} className="text-white" style={{ opacity: 1, filter: 'drop-shadow(0 1px 2px rgba(0, 0, 0, 0.3))' }} />
          </motion.button>
        )}
      </div>
    </>
  );
};

